// src/quiz/gre-mock4/quantitativeData.js
// GRE Mock 4 — Quantitative Reasoning (harder set).
// Section 1: 12 questions (21 min). Section 2: 15 questions (26 min).
// Question format: see ../greQuestionTypes.js
import { QC_OPTIONS } from "../greQuestionTypes";

const QC_QUESTION = "Compare Quantity A and Quantity B, using additional information centered above the two quantities if such information is given.";
const [QC_A, QC_B, QC_EQUAL, QC_UNKNOWN] = QC_OPTIONS;

const STAFF_TABLE = {
  caption: "Employees of Firm Z by Department and Highest Degree Earned",
  headers: ["Department", "Bachelor's", "Master's", "Doctorate", "Total"],
  rows: [
    ["Research", "12", "30", "18", "60"],
    ["Engineering", "45", "35", "5", "85"],
    ["Sales", "50", "15", "0", "65"],
    ["Operations", "28", "10", "2", "40"],
    ["Total", "135", "90", "25", "250"],
  ],
};

// ================= SECTION 1 =================

export const section1Questions = [
  {
    id: 1,
    question: QC_QUESTION,
    given: "x < 0",
    quantityA: "x³ − x",
    quantityB: "x² − x",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "A − B = x³ − x² = x²(x − 1). For x < 0, x² > 0 and x − 1 < 0, so the difference is negative: Quantity B is greater.",
  },
  {
    id: 2,
    question: QC_QUESTION,
    quantityA: "The greatest prime factor of 2¹⁰ − 1",
    quantityB: "The greatest prime factor of 2⁸ − 1",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "2¹⁰ − 1 = 1,023 = 3 × 11 × 31, and 2⁸ − 1 = 255 = 3 × 5 × 17. Compare 31 with 17.",
  },
  {
    id: 3,
    question: QC_QUESTION,
    given: "p and q are positive numbers and p/q = 3/7.",
    quantityA: "(p + 3)/(q + 7)",
    quantityB: "3/7",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "Write p = 3k and q = 7k. Then (3k + 3)/(7k + 7) = 3(k + 1)/(7(k + 1)) = 3/7.",
  },
  {
    id: 4,
    question: QC_QUESTION,
    given: "A point is chosen at random from the interior of a square with side length 2.",
    quantityA: "The probability that the point is within 1 unit of the center of the square",
    quantityB: "3/4",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "Points within 1 unit of the center form a circle of radius 1 (area π), which fits inside the square (area 4). The probability is π/4 ≈ 0.785 > 0.75.",
  },
  {
    id: 5,
    question: "If x + y = 7 and x² + y² = 29, what is the value of x³ + y³?",
    options: ["119", "133", "143", "189", "210"],
    answer: "133",
    explanation: "(x + y)² = x² + 2xy + y² gives 49 = 29 + 2xy, so xy = 10. Then x³ + y³ = (x + y)³ − 3xy(x + y) = 343 − 210 = 133.",
  },
  {
    id: 6,
    question: "A stock's price fell 20 percent on Monday and rose 10 percent on Tuesday. By approximately what percent must it rise on Wednesday to return to its price before Monday?",
    options: ["10.0%", "12.0%", "13.6%", "15.0%", "18.2%"],
    answer: "13.6%",
    explanation: "After two days the price is 0.8 × 1.1 = 0.88 of the original. It must be multiplied by 1/0.88 ≈ 1.136, an increase of about 13.6%. Simply adding the changes (−20% + 10%) gives the trap answer 10%.",
  },
  {
    id: 7,
    type: "numeric",
    question: "Working together at their respective constant rates, Ana and Ben can paint a fence in 6 hours. Working alone, Ana takes 5 hours less than Ben does. How many hours does it take Ben to paint the fence alone?",
    answer: 15,
    explanation: "Let Ben take b hours, Ana b − 5. Then 1/b + 1/(b − 5) = 1/6, which gives b² − 17b + 30 = 0, so b = 15 or b = 2. b = 2 would give Ana a negative time, so b = 15 (Ana takes 10; 1/15 + 1/10 = 1/6).",
  },
  {
    id: 8,
    type: "multi",
    question: "Which of the following numbers are greater than 1/3?",
    options: ["0.33", "7/20", "√0.1", "(0.6)²", "5/16", "1/2.9"],
    answer: ["7/20", "(0.6)²", "1/2.9"],
    explanation: "1/3 ≈ 0.3333. 0.33 is less; 7/20 = 0.35; √0.1 ≈ 0.316; (0.6)² = 0.36; 5/16 = 0.3125; 1/2.9 ≈ 0.345 (dividing by less than 3 gives more than 1/3).",
  },
  {
    id: 9,
    question: "A right triangle has legs of length 6 and 8. What is the length of the altitude drawn to the hypotenuse?",
    options: ["4.2", "4.5", "4.8", "5.0", "5.2"],
    answer: "4.8",
    explanation: "The hypotenuse is 10 and the area is ½ × 6 × 8 = 24. Using the hypotenuse as base: ½ × 10 × h = 24, so h = 4.8.",
  },
  {
    id: 10,
    table: STAFF_TABLE,
    question: "If one employee is selected at random from those whose highest degree is a master's or a doctorate, what is the probability that the employee works in Research?",
    options: ["48/115", "48/250", "18/25", "2/5", "12/25"],
    answer: "48/115",
    explanation: "Employees with a master's or doctorate: 90 + 25 = 115. Of these, Research has 30 + 18 = 48. The probability is 48/115. 48/250 wrongly uses all employees as the denominator.",
  },
  {
    id: 11,
    type: "numeric",
    table: STAFF_TABLE,
    question: "What percent of Engineering employees hold a master's degree or a doctorate as their highest degree? Give your answer to the nearest whole percent. (Enter the number only.)",
    answer: 47,
    explanation: "(35 + 5)/85 = 40/85 ≈ 47.06%, i.e. 47%.",
  },
  {
    id: 12,
    type: "multi",
    table: STAFF_TABLE,
    question: "In which departments do employees whose highest degree is a master's or a doctorate make up at least 25 percent of the department?",
    options: ["Research", "Engineering", "Sales", "Operations"],
    answer: ["Research", "Engineering", "Operations"],
    explanation: "Research 48/60 = 80%; Engineering 40/85 ≈ 47%; Sales 15/65 ≈ 23%; Operations 12/40 = 30%. Only Sales falls below 25%.",
  },
];

// ================= SECTION 2 =================

export const section2Questions = [
  {
    id: 13,
    question: QC_QUESTION,
    given: "n is an odd integer.",
    quantityA: "The remainder when n² is divided by 8",
    quantityB: "1",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "n = 2k + 1 gives n² = 4k(k + 1) + 1. k(k + 1) is always even, so 4k(k + 1) is a multiple of 8 and the remainder is always 1.",
  },
  {
    id: 14,
    question: QC_QUESTION,
    quantityA: "8^(2/3) + 27^(−1/3)",
    quantityB: "4.3",
    options: QC_OPTIONS,
    answer: QC_A,
    explanation: "8^(2/3) = (∛8)² = 4 and 27^(−1/3) = 1/∛27 = 1/3. The sum is 4.333…, which is greater than 4.3.",
  },
  {
    id: 15,
    question: QC_QUESTION,
    given: "a, b and c are consecutive integers, and a < b < c.",
    quantityA: "ac",
    quantityB: "b²",
    options: QC_OPTIONS,
    answer: QC_B,
    explanation: "a = b − 1 and c = b + 1, so ac = b² − 1, which is always less than b², whatever the sign of b.",
  },
  {
    id: 16,
    question: QC_QUESTION,
    given: "The probability that event E occurs is 0.6, and the probability that event F occurs is 0.5.",
    quantityA: "The probability that both E and F occur",
    quantityB: "0.1",
    options: QC_OPTIONS,
    answer: QC_UNKNOWN,
    explanation: "P(E and F) = 0.6 + 0.5 − P(E or F). Since P(E or F) ≤ 1, P(E and F) ≥ 0.1, and it can be as large as 0.5. It may equal 0.1 or exceed it; nothing says E and F are independent.",
  },
  {
    id: 17,
    question: QC_QUESTION,
    given: "A circle with center O has radius 6, and chord AB has length 6.",
    quantityA: "The length of minor arc AB",
    quantityB: "2π",
    options: QC_OPTIONS,
    answer: QC_EQUAL,
    explanation: "OA = OB = AB = 6, so triangle OAB is equilateral and the central angle is 60°. Arc length = (60/360) × 2π(6) = 2π.",
  },
  {
    id: 18,
    question: "Let f(x) = 3x − 2 and g(x) = x² + 1. What is the sum of all values of a for which f(g(a)) = g(f(a))?",
    options: ["0", "1", "4/3", "2", "3"],
    answer: "2",
    explanation: "f(g(a)) = 3a² + 1 and g(f(a)) = (3a − 2)² + 1 = 9a² − 12a + 5. Setting them equal gives 6a² − 12a + 4 = 0, i.e. 3a² − 6a + 2 = 0. The discriminant is positive, so there are two real roots, whose sum is 6/3 = 2.",
  },
  {
    id: 19,
    question: "Two different numbers are selected at random from the set {1, 2, 3, …, 10}. What is the probability that their product is even?",
    options: ["1/2", "2/3", "7/9", "4/5", "8/9"],
    answer: "7/9",
    explanation: "The product is odd only if both numbers are odd: C(5, 2)/C(10, 2) = 10/45 = 2/9. So P(even) = 1 − 2/9 = 7/9.",
  },
  {
    id: 20,
    type: "numeric",
    question: "Two trains 300 miles apart travel toward each other on parallel tracks, one at 70 miles per hour and the other at 50 miles per hour. A bird starts at the first train and flies back and forth between the trains at a constant 90 miles per hour until the trains meet. How many miles does the bird fly in total?",
    answer: 225,
    explanation: "Rather than summing the back-and-forth legs, find the time: the trains close at 120 mph, so they meet after 300/120 = 2.5 hours. The bird flies 90 × 2.5 = 225 miles.",
  },
  {
    id: 21,
    question: "How many four-digit positive integers have four different digits and are divisible by 5?",
    options: ["448", "504", "896", "952", "1,008"],
    answer: "952",
    explanation: "The last digit is 0 or 5. Ending in 0: 9 × 8 × 7 = 504 choices for the first three digits. Ending in 5: the first digit cannot be 0 or 5 (8 choices), then 8 × 7: 448. Total 504 + 448 = 952.",
  },
  {
    id: 22,
    question: "What is the sum of all solutions of the equation |x − 4| = 2x + 1?",
    options: ["−5", "−4", "1", "6", "The equation has no solutions."],
    answer: "1",
    explanation: "If x ≥ 4: x − 4 = 2x + 1 gives x = −5, which contradicts x ≥ 4. If x < 4: 4 − x = 2x + 1 gives x = 1, which checks (|1 − 4| = 3 = 2·1 + 1). The only solution is 1; −4 comes from wrongly keeping the extraneous root.",
  },
  {
    id: 23,
    type: "multi",
    question: "For which of the following values of k does the equation x² + kx + 9 = 0 have two distinct real solutions?",
    options: ["−7", "−6", "0", "5", "6", "6.5"],
    answer: ["−7", "6.5"],
    explanation: "Two distinct real roots require the discriminant k² − 36 > 0, i.e. |k| > 6. k = ±6 gives a single (repeated) root.",
  },
  {
    id: 24,
    type: "numeric",
    question: "In a sequence, a₁ = 2, a₂ = 3, and aₙ = aₙ₋₁ · aₙ₋₂ for all n ≥ 3. What is the value of a₅?",
    answer: 108,
    explanation: "a₃ = 3 × 2 = 6, a₄ = 6 × 3 = 18, a₅ = 18 × 6 = 108.",
  },
  {
    id: 25,
    question: "What is the area of a regular hexagon with side length 4?",
    options: ["16√3", "24√3", "32√3", "48", "48√3"],
    answer: "24√3",
    explanation: "A regular hexagon is six equilateral triangles of side 4, each with area (√3/4) × 16 = 4√3. Total: 24√3.",
  },
  {
    id: 26,
    question: "In a class of 50 students, the average (arithmetic mean) score of the 20 men on an exam was 72, and the average score of all 50 students was 78. What was the average score of the women?",
    options: ["80", "81", "82", "84", "85"],
    answer: "82",
    explanation: "Total of all scores = 50 × 78 = 3,900; men's total = 20 × 72 = 1,440. Women's total = 2,460 over 30 women: 82.",
  },
  {
    id: 27,
    type: "numeric",
    question: "How many positive divisors does 720 have?",
    answer: 30,
    explanation: "720 = 2⁴ × 3² × 5. The number of divisors is (4 + 1)(2 + 1)(1 + 1) = 30.",
  },
];
